import { create } from 'zustand';
import { AD_DEMOS, type AdDemo } from '../journey/demos';
import { APP_TYPES, defaultAnswers, TYPES, type Answer, type Answers, type AppType } from '../journey/questions';

/**
 * What V7's "What is it?" and its questions hold between screens (docs/superpowers/specs/2026-10-16-v7-build.md):
 * the type, the sentence (and whether it was edited), the ad's demo it came from, and the answers.
 *
 * Kept in this tab's sessionStorage so a reload on the questions finds them, and nowhere else: it
 * is somebody's own words, and it is planned from the draft (state/draft.ts) once they press Build.
 */
export interface What {
  type: AppType;
  prompt: string;
  /** What the field was last filled with by us: their edit is anything else, and a type switch keeps it. */
  filled: string;
  /** The ad's demo this began from, by slug (journey/demos.ts), or null. */
  demo: string | null;
  answers: Answers;
  /** Variant b's screen, from 0 (journey/questions.ts `TypeDef.b`). */
  step: number;
}

interface WhatStore extends What {
  /** Begin from an ad's demo, or from nothing (Other, an empty field). */
  begin: (demo: AdDemo | null) => void;
  setPrompt: (text: string) => void;
  /** Switch the type: its example replaces the sentence unless they have edited it; its answers start fresh. */
  chooseType: (type: AppType, group: 'a' | 'b') => void;
  answer: (id: string, value: Answer) => void;
  /** Make sure the answers are the type's (for the group), keeping any already given. */
  ensureAnswers: (group: 'a' | 'b') => void;
  setStep: (step: number) => void;
}

const KEY = 'saddle.what';

const blank = (): What => ({ type: 'other', prompt: '', filled: '', demo: null, answers: {}, step: 0 });

function read(): What {
  try {
    const raw = JSON.parse(sessionStorage.getItem(KEY) ?? 'null') as Partial<What> | null;
    if (!raw || !APP_TYPES.includes(raw.type as AppType)) return blank();
    return {
      type: raw.type as AppType,
      prompt: typeof raw.prompt === 'string' ? raw.prompt : '',
      filled: typeof raw.filled === 'string' ? raw.filled : '',
      demo: typeof raw.demo === 'string' ? raw.demo : null,
      answers: raw.answers && typeof raw.answers === 'object' ? (raw.answers as Answers) : {},
      step: typeof raw.step === 'number' && raw.step >= 0 ? raw.step : 0,
    };
  } catch {
    return blank();
  }
}

/** The sentence a type starts with: the ad's own for the ad's type, else the type's example. */
export function exampleFor(type: AppType, demoSlug: string | null): string {
  const demo = demoSlug ? AD_DEMOS.find((d) => d.slug === demoSlug) : undefined;
  return demo && demo.type === type ? demo.sentence : TYPES[type].example;
}

/** Whether they have changed the sentence we put there. */
export const edited = (w: Pick<What, 'prompt' | 'filled'>): boolean => w.prompt.trim() !== w.filled.trim();

export const useWhat = create<WhatStore>((set, get) => ({
  ...read(),
  begin: (demo) => {
    const type = demo?.type ?? 'other';
    const prompt = demo ? demo.sentence : '';
    set({ type, prompt, filled: prompt, demo: demo?.slug ?? null, answers: {}, step: 0 });
  },
  setPrompt: (text) => set({ prompt: text }),
  chooseType: (type, group) => {
    const now = get();
    if (type === now.type) return;
    const keep = edited(now);
    const example = exampleFor(type, now.demo);
    set({
      type,
      ...(keep ? {} : { prompt: example, filled: example }),
      answers: defaultAnswers(type, group),
      step: 0,
    });
  },
  answer: (id, value) => set({ answers: { ...get().answers, [id]: value } }),
  ensureAnswers: (group) => {
    const now = get();
    const defaults = defaultAnswers(now.type, group);
    const merged: Answers = { ...defaults };
    for (const id of Object.keys(defaults)) if (id in now.answers) merged[id] = now.answers[id];
    set({ answers: merged });
  },
  setStep: (step) => set({ step }),
}));

useWhat.subscribe((s) => {
  try {
    const kept: What = { type: s.type, prompt: s.prompt, filled: s.filled, demo: s.demo, answers: s.answers, step: s.step };
    sessionStorage.setItem(KEY, JSON.stringify(kept));
  } catch {
    /* held for this page load only */
  }
});

/** Test-only: forget it all, here and in storage. */
export function _resetWhatForTest(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* nothing kept */
  }
  useWhat.setState(blank());
}
